import React, { createContext, Dispatch, ReactNode, SetStateAction, useContext, useEffect, useReducer, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

type Props = {
  children: ReactNode;
};

type Breadcrumbs = { label: string; path: string }[];

type SideNavCollapse = {
  dashboard: boolean;
  salesOrders: boolean;
  purchaseOrders: boolean;
  backoffice: boolean;
  finance: boolean;
};

const defaultSideNavCollapse = { dashboard: true, salesOrders: true, purchaseOrders: true, backoffice: true, finance: true };

type SideNavMenuSelectState = {
  dashboard: boolean;
  salesOrders: boolean;
  purchaseOrders: boolean;
  backoffice: boolean;
  approval: boolean;
  finance: boolean;
  setting: boolean;
};

const defaultSideNavMenuSelectState: SideNavMenuSelectState = {
  dashboard: false,
  salesOrders: false,
  purchaseOrders: false,
  backoffice: false,
  approval: false,
  finance: false,
  setting: false,
};

type SideNavMenuAction = 'dashboard' | 'salesOrders' | 'purchaseOrders' | 'backoffice' | 'approval' | 'finance' | 'setting' | 'user_setting';

const reducer = (state: SideNavMenuSelectState, action: SideNavMenuAction): SideNavMenuSelectState => {
  switch (action) {
    case 'dashboard':
      return { ...defaultSideNavMenuSelectState, dashboard: true };
    case 'salesOrders':
      return { ...defaultSideNavMenuSelectState, salesOrders: true };
    case 'purchaseOrders':
      return { ...defaultSideNavMenuSelectState, purchaseOrders: true };
    case 'backoffice':
      return { ...defaultSideNavMenuSelectState, backoffice: true };
    case 'approval':
      return { ...defaultSideNavMenuSelectState, approval: true };
    case 'finance':
      return { ...defaultSideNavMenuSelectState, finance: true };
    case 'setting':
      return { ...defaultSideNavMenuSelectState, setting: true };
    default:
      return { ...defaultSideNavMenuSelectState };
  }
};

export type NavStateContextType = {
  showSideNav: boolean;
  setShowSideNav: Dispatch<SetStateAction<boolean>>;
  minimumSideNav: boolean;
  setMinimumSideNav: Dispatch<SetStateAction<boolean>>;
  sideNavCollapse: SideNavCollapse;
  setSideNavCollapse: Dispatch<SetStateAction<SideNavCollapse>>;
  title: string;
  setTitle: Dispatch<SetStateAction<string>>;
  breadcrumbs: Breadcrumbs;
  setBreadcrumbs: Dispatch<SetStateAction<Breadcrumbs>>;
  sideNavMenuSelectState: SideNavMenuSelectState;
};

export const NavStateContext = createContext<NavStateContextType>({
  showSideNav: false,
  setShowSideNav: () => {},
  minimumSideNav: false,
  setMinimumSideNav: () => {},
  sideNavCollapse: defaultSideNavCollapse,
  setSideNavCollapse: () => {},
  title: '',
  setTitle: () => {},
  breadcrumbs: [],
  setBreadcrumbs: () => {},
  sideNavMenuSelectState: defaultSideNavMenuSelectState,
});

export const NavStateProvider = ({ children }: Props) => {
  const location = useLocation();
  const [showSideNav, setShowSideNav] = useState<boolean>(false);
  const [minimumSideNav, setMinimumSideNav] = useState<boolean>(true);
  const [sideNavCollapse, setSideNavCollapse] = useState<SideNavCollapse>(defaultSideNavCollapse);
  const [title, setTitle] = useState<string>('');
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumbs>([]);
  const [sideNavMenuSelectState, dispatchSideNavMenuSelect] = useReducer(reducer, defaultSideNavMenuSelectState);

  useEffect(() => {
    const paths = location.pathname.split('/');

    if (paths.length == 3 || paths[3] == `dashboard`) {
      dispatchSideNavMenuSelect('dashboard');
      setSideNavCollapse({ ...sideNavCollapse, dashboard: false });
    }
    if (paths[5] === `sales_orders`) {
      dispatchSideNavMenuSelect('salesOrders');
      setSideNavCollapse({ ...sideNavCollapse, salesOrders: false });
    }
    if (paths[5] === `purchase_orders`) {
      dispatchSideNavMenuSelect('purchaseOrders');
      setSideNavCollapse({ ...sideNavCollapse, purchaseOrders: false });
    }
    if (paths[3] === `backoffice`) {
      dispatchSideNavMenuSelect('backoffice');
      setSideNavCollapse({ ...sideNavCollapse, backoffice: false });
    }
    if (paths[3] === `approval`) {
      dispatchSideNavMenuSelect('approval');
    }
    if (paths[3] === `finance`) {
      dispatchSideNavMenuSelect('finance');
      setSideNavCollapse({ ...sideNavCollapse, finance: false });
    }
    if (paths[3] === `setting`) {
      dispatchSideNavMenuSelect('setting');
    }
    if (paths[3] === `user_setting`) {
      dispatchSideNavMenuSelect('user_setting');
    }
  }, [location]);

  return (
    <NavStateContext.Provider
      value={{
        showSideNav,
        setShowSideNav,
        minimumSideNav,
        setMinimumSideNav,
        sideNavCollapse,
        setSideNavCollapse,
        title,
        setTitle,
        breadcrumbs,
        setBreadcrumbs,
        sideNavMenuSelectState,
      }}
    >
      {children}
    </NavStateContext.Provider>
  );
};

export const useNavState = () => {
  return useContext(NavStateContext);
};
